import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import InputField from '../../components/Forms/Input/InputField';
import { useUserProfile } from '../../hooks/useUserProfile';
import { RootState } from '../../store/store';

const PersonalInfo = () => {
  const { handleUpdatePersonalInfo } = useUserProfile();

  // =========================================================
  // 👤 Usuario desde Redux (AuthInitializer)
  // =========================================================

  const userProfile = useSelector(
    (state: RootState) => state.userProfile.userProfile?.data
  );

  // =========================================================
  // 📝 Formulario local
  // =========================================================

  const [formData, setFormData] = useState({
    id: 0,
    username: '',
    email: '',
    firstName: '',
    middleName: '',
    lastName: '',
    secondLastName: '',
    phoneNumber: '',
    dui: '',
  });

  // =========================================================
  // 🔄 Poblar formulario desde Redux
  // =========================================================

  useEffect(() => {
    if (!userProfile?.id) return;

    setFormData({
      id: userProfile.id || 0,
      username: userProfile.username || '',
      email: userProfile.email || '',
      firstName: userProfile.firstName || '',
      middleName: userProfile.middleName || '',
      lastName: userProfile.lastName || '',
      secondLastName: userProfile.secondLastName || '',
      phoneNumber: userProfile.phoneNumber || '',
      dui: userProfile.dui || '',
    });
  }, [userProfile]);

  // =========================================================
  // 🔄 Change handler
  // =========================================================

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // =========================================================
  // ✅ Guardar cambios
  // =========================================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.id) return;

    await handleUpdatePersonalInfo(formData.id, formData);
  };

  // =========================================================
  // 🎨 UI
  // =========================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-title-xsm font-semibold text-black dark:text-white">
          Información Personal
        </h2>

        <p className="mt-1 text-sm text-body dark:text-bodydark">
          Aquí puedes actualizar tu información personal y de contacto.
        </p>
      </div>

      {/* Formulario */}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-5 md:grid-cols-2"
      >
        {/* Usuario */}
        <InputField
          label="Nombre de Usuario"
          name="username"
          type="text"
          value={formData.username}
          onChange={(e) => handleChange('username', e.target.value)}
          iconKey="user"
          disabled
          error=""
        />

        {/* Teléfono */}
        <InputField
          label="Número de Teléfono"
          name="phoneNumber"
          type="text"
          value={formData.phoneNumber}
          onChange={(e) => handleChange('phoneNumber', e.target.value)}
          placeholder="+503 6441 0869"
          iconKey="phone"
          error=""
        />

        {/* Email */}
        <InputField
          label="Correo Electrónico"
          name="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          iconKey="email"
          disabled
          error=""
        />

        {/* DUI */}
        <InputField
          label="DUI"
          name="dui"
          type="text"
          value={formData.dui}
          onChange={(e) => handleChange('dui', e.target.value)}
          placeholder="12345678-9"
          iconKey="idCard"
          error=""
        />

        {/* Primer Nombre */}
        <InputField
          label="Primer Nombre"
          name="firstName"
          type="text"
          value={formData.firstName}
          onChange={(e) => handleChange('firstName', e.target.value)}
          placeholder="Juan"
          iconKey="edit"
          error=""
        />

        {/* Segundo Nombre */}
        <InputField
          label="Segundo Nombre"
          name="middleName"
          type="text"
          value={formData.middleName}
          onChange={(e) => handleChange('middleName', e.target.value)}
          placeholder="Carlos"
          iconKey="edit"
          error=""
        />

        {/* Primer Apellido */}
        <InputField
          label="Primer Apellido"
          name="lastName"
          type="text"
          value={formData.lastName}
          onChange={(e) => handleChange('lastName', e.target.value)}
          placeholder="Pérez"
          iconKey="edit"
          error=""
        />

        {/* Segundo Apellido */}
        <InputField
          label="Segundo Apellido"
          name="secondLastName"
          type="text"
          value={formData.secondLastName}
          onChange={(e) => handleChange('secondLastName', e.target.value)}
          placeholder="Rodríguez"
          iconKey="edit"
          error=""
        />

        {/* Acción */}
        <div className="flex justify-end border-t border-stroke pt-5 dark:border-strokedark md:col-span-2">
          <button
            type="submit"
            className="
              rounded-md
              bg-primary
              px-6 py-2.5
              text-sm font-medium
              text-white
              shadow-default
              transition-all duration-200
              hover:opacity-90
            "
          >
            Guardar Cambios
          </button>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfo;
